import React, { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, Table } from "reactstrap";
import { toast } from "react-toastify";
import PreLoader from "../../../../share-components/ui/preLoader";
import DropZone from "../../../../share-components/upload/dropzone";
import {
  getProductApi,
  getSizeColourMediaApi,
  uploadSizeColourImageApi,
  uploadSizeColourVideoApi,
  deleteSizeColourMediaApi,
  deleteSizeColourVideoApi,
  getFitModelsApi,
  assignSizeColourFitModelApi,
} from "../../../../services/manage-product/productService";

const cellKey = (sizeMatrixId, colourId) => `${sizeMatrixId}_${colourId}`;

const validateImage = (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      const megapixels = (img.width * img.height) / 1_000_000; // Convert to MP
      if (megapixels > 25) {
        reject("Image size should not exceed 25 megapixels.");
      } else {
        resolve("Valid Image");
      }
    };
    img.onerror = () => reject("Invalid image file.");
  });
};

// The shared axios interceptor (src/utils/api.jsx) already shows a toast itself for 422
// and 401 responses (and rejects with the raw response body, not an Error, in those two
// cases) — only re-toast here when the rejection is a real AxiosError that wasn't already
// surfaced globally, so mutation failures aren't double-toasted.
const notifyMutationError = (err, fallback) => {
  if (err && err.isAxiosError && err.response?.status !== 404) {
    toast.error(err.response?.data?.message || err.message || fallback);
  }
};

const buildMediaFormData = (sizeMatrixId, colourId, fileField, file) => {
  const data = new FormData();
  data.append("size_matrix_id", sizeMatrixId);
  data.append("colour_id", colourId);
  data.append(fileField, file);
  return data;
};

const TILE = 96;

const tileWrapStyle = {
  width: TILE,
  height: TILE,
  borderRadius: 8,
  overflow: "hidden",
  position: "relative",
  flexShrink: 0,
  border: "1px solid #eee",
  background: "#fafafa",
};

const dropzoneTileStyle = {
  width: TILE,
  height: TILE,
  padding: 0,
  margin: 0,
  borderRadius: 8,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 11,
  lineHeight: 1.2,
  textAlign: "center",
};

const deleteBadgeStyle = {
  position: "absolute",
  top: 4,
  right: 4,
  width: 22,
  height: 22,
  borderRadius: "50%",
  border: "none",
  background: "rgba(0,0,0,0.55)",
  color: "#fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 11,
  cursor: "pointer",
  padding: 0,
};

const kindBadgeStyle = {
  position: "absolute",
  bottom: 4,
  left: 4,
  padding: "1px 5px",
  borderRadius: 3,
  background: "rgba(0,0,0,0.55)",
  color: "#fff",
  fontSize: 9,
  letterSpacing: 0.3,
  textTransform: "uppercase",
};

// Keeps the Size column readable while the row scrolls horizontally under many colours.
const stickyColStyle = (isHeader) => ({
  position: "sticky",
  left: 0,
  background: isHeader ? "#f8f9fa" : "#fff",
  zIndex: isHeader ? 3 : 2,
  minWidth: 70,
});

const TileSpinner = () => (
  <div style={{ ...tileWrapStyle, display: "flex", alignItems: "center", justifyContent: "center" }}>
    <div className="loader-2"></div>
  </div>
);

const MediaTile = ({ kind, fileUrl, busy, onDelete, dropZoneProps }) => {
  if (busy) return <TileSpinner />;

  if (fileUrl) {
    return (
      <div style={tileWrapStyle}>
        {kind === "video" ? (
          <video style={{ width: "100%", height: "100%", objectFit: "cover" }} autoPlay muted loop>
            <source src={fileUrl} type="video/mp4" />
          </video>
        ) : (
          <img style={{ width: "100%", height: "100%", objectFit: "cover" }} src={fileUrl} alt="" />
        )}
        <span style={kindBadgeStyle}>{kind === "video" ? "mp4" : "img"}</span>
        <button type="button" style={deleteBadgeStyle} title={`Delete ${kind}`} onClick={onDelete}>
          <i className="icon-trash"></i>
        </button>
      </div>
    );
  }

  return (
    <div style={tileWrapStyle}>
      <DropZone {...dropZoneProps} isInvoiceUpload customStyle={dropzoneTileStyle} customContainerStles={{ width: TILE, height: TILE }} />
    </div>
  );
};

const ManageSizeColourMediaModal = ({ open, onClose, productId, productCode, productColour }) => {
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [sizes, setSizes] = useState([]);
  const [colours, setColours] = useState([]);
  const [fitModels, setFitModels] = useState([]);
  const [mediaMap, setMediaMap] = useState({});

  // Keyed by cellKey -> {image: bool, video: bool}; a mutation on one cell must never
  // disable the rest of the grid, so busy state is tracked per cell, not globally.
  const [cellBusy, setCellBusy] = useState({});

  const applyMediaList = (list) => {
    const map = {};
    (Array.isArray(list) ? list : []).forEach((item) => {
      map[cellKey(item.size_matrix_id, item.colour_id)] = item;
    });
    setMediaMap(map);
  };

  const fetchAll = () => {
    if (!productId) return;
    setLoading(true);
    setLoadError("");
    Promise.all([getProductApi(productId), getSizeColourMediaApi(productId), getFitModelsApi()])
      .then(([editRes, mediaRes, fitModelRes]) => {
        const product = editRes.response?.product || {};
        setSizes(Array.isArray(product.size) ? product.size : []);
        setColours(Array.isArray(product.color) ? product.color : []);
        setFitModels(Array.isArray(fitModelRes.response) ? fitModelRes.response : []);
        applyMediaList(mediaRes.response);
      })
      .catch((err) => {
        setLoadError(err?.response?.data?.message || err?.message || "Failed to load size/colour media.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    // Fully clear stale state so a later reopen never flashes a previous product's grid,
    // whether we're closing or switching to a different productId.
    setSizes([]);
    setColours([]);
    setFitModels([]);
    setMediaMap({});
    setCellBusy({});
    setLoadError("");
    setLoading(false);

    if (open && productId) {
      fetchAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, productId]);

  const setBusy = (key, type, isBusy) => {
    setCellBusy((prev) => ({ ...prev, [key]: { ...prev[key], [type]: isBusy } }));
  };

  const onChangeImageFile = async (key, sizeMatrixId, colourId, files, rejections) => {
    if (rejections.length > 0) {
      toast.error("one or many of the uploaded file size more than 20MB for image and 100MB for video");
      return;
    }
    if (files.length === 0) return;

    const file = files[0];
    let fileSizeError = false;
    const validationErrors = [];

    if (/^image\//.test(file.type) && file.size > 20971520) {
      fileSizeError = true;
    }
    if (/^image\//.test(file.type)) {
      try {
        await validateImage(file);
      } catch (error) {
        validationErrors.push(error);
      }
    }

    if (fileSizeError || validationErrors.length > 0) {
      toast.error(fileSizeError ? "Image exceeds the 20MB size limit." : validationErrors.join(", "));
      return;
    }

    setBusy(key, "image", true);
    uploadSizeColourImageApi(productId, buildMediaFormData(sizeMatrixId, colourId, "image", file))
      .then(({ response }) => {
        applyMediaList(response);
        toast.success("Image uploaded successfully.");
      })
      .catch((err) => notifyMutationError(err, "Failed to upload the image."))
      .finally(() => setBusy(key, "image", false));
  };

  const onChangeVideoFile = async (key, sizeMatrixId, colourId, files, rejections) => {
    if (rejections.length > 0) {
      toast.error("one or many of the uploaded file size more than 20MB for image and 100MB for video");
      return;
    }
    if (files.length === 0) return;

    const file = files[0];
    if (file.type !== "video/mp4" || file.size > 104857600) {
      toast.error("Video must be an mp4 file under 100MB.");
      return;
    }

    setBusy(key, "video", true);
    uploadSizeColourVideoApi(productId, buildMediaFormData(sizeMatrixId, colourId, "video", file))
      .then(({ response }) => {
        applyMediaList(response);
        toast.success("Video uploaded successfully.");
      })
      .catch((err) => notifyMutationError(err, "Failed to upload the video."))
      .finally(() => setBusy(key, "video", false));
  };

  const onDeleteImage = (key, item) => {
    setBusy(key, "image", true);
    deleteSizeColourMediaApi(item.id)
      .then(({ response }) => {
        applyMediaList(response);
        toast.success("Media deleted successfully.");
      })
      .catch((err) => notifyMutationError(err, "Failed to delete the media."))
      .finally(() => setBusy(key, "image", false));
  };

  const onDeleteVideo = (key, item) => {
    setBusy(key, "video", true);
    deleteSizeColourVideoApi(item.id)
      .then(({ response }) => {
        applyMediaList(response);
        toast.success("Video deleted successfully.");
      })
      .catch((err) => notifyMutationError(err, "Failed to delete the video."))
      .finally(() => setBusy(key, "video", false));
  };

  const onChangeFitModel = (key, item, fitModelId) => {
    setBusy(key, "fitModel", true);
    assignSizeColourFitModelApi(item.id, { fit_model_id: fitModelId || null })
      .then(({ response }) => {
        applyMediaList(response);
      })
      .catch((err) => notifyMutationError(err, "Failed to assign the fit model."))
      .finally(() => setBusy(key, "fitModel", false));
  };

  return (
    <Modal isOpen={open} toggle={onClose} className="modal-dialog-centered" size="xl">
      <ModalHeader toggle={onClose} tag="div">
        <div>Size-wise Fit Guide Media</div>
        {(productCode || (Array.isArray(productColour) && productColour.filter(Boolean).length > 0)) && (
          <div style={{ fontSize: "0.8rem", fontWeight: 400, color: "#888", marginTop: 2 }}>
            {[productCode, Array.isArray(productColour) ? productColour.filter(Boolean).join(", ") : productColour]
              .filter(Boolean)
              .join(" · ")}
          </div>
        )}
      </ModalHeader>
      <ModalBody style={{ minHeight: 200, position: "relative" }}>
        {loading && <PreLoader withoutText />}

        {!loading && loadError && (
          <div className="alert alert-danger">
            <div className="font-weight-bold mb-1">Size/colour media could not be loaded</div>
            <div className="mb-2">{loadError}</div>
            <button type="button" className="btn btn-sm btn-outline-danger" onClick={fetchAll}>
              Retry
            </button>
          </div>
        )}

        {!loading && !loadError && (sizes.length === 0 || colours.length === 0) && (
          <p>Select colours and sizes first</p>
        )}

        {!loading && !loadError && sizes.length > 0 && colours.length > 0 && (
          <div className="table-responsive">
            <Table bordered hover className="mb-0">
              <thead>
                <tr>
                  <th style={stickyColStyle(true)}>Size</th>
                  {colours.map((colour) => (
                    <th key={colour.id} style={{ minWidth: TILE * 2 + 40, whiteSpace: "nowrap" }}>
                      {colour.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sizes.map((size) => (
                  <tr key={size.id}>
                    <td style={stickyColStyle(false)}>
                      <strong>{size.name}</strong>
                    </td>
                    {colours.map((colour) => {
                      const key = cellKey(size.id, colour.id);
                      const item = mediaMap[key];
                      const busy = cellBusy[key] || {};
                      const hasImage = !!(item && item.image_path_url);

                      return (
                        <td key={key} className="align-middle">
                          <div className="d-flex" style={{ gap: 8 }}>
                            <MediaTile
                              kind="image"
                              fileUrl={item && item.image_path_url}
                              busy={busy.image}
                              onDelete={() => onDeleteImage(key, item)}
                              dropZoneProps={{
                                extension: "image/*",
                                placeholder: "+ Image",
                                onChangeFile: (files, rejections) => onChangeImageFile(key, size.id, colour.id, files, rejections),
                              }}
                            />

                            {/* Video is independent of Shopify publish status — only an
                                existing image is required, matching the backend gate. */}
                            {hasImage && (
                              <MediaTile
                                kind="video"
                                fileUrl={item && item.video_path_url}
                                busy={busy.video}
                                onDelete={() => onDeleteVideo(key, item)}
                                dropZoneProps={{
                                  extension: ".mp4",
                                  placeholder: "+ Video",
                                  onChangeFile: (files, rejections) => onChangeVideoFile(key, size.id, colour.id, files, rejections),
                                }}
                              />
                            )}
                          </div>

                          {hasImage && (
                            <select
                              className="form-control form-control-sm mt-2"
                              style={{ fontSize: 11, maxWidth: TILE * 2 + 8 }}
                              value={item.fit_model_id || ""}
                              disabled={!!busy.fitModel}
                              onChange={(e) => onChangeFitModel(key, item, e.target.value)}
                            >
                              <option value="">No fit model</option>
                              {fitModels.map((fitModel) => (
                                <option key={fitModel.id} value={fitModel.id}>{fitModel.name}</option>
                              ))}
                            </select>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};

export default ManageSizeColourMediaModal;
